import { CreditScore, PrimaryGoal } from 'src/types/inputs';
import { Input, Inputs } from 'src/lib/constants';
import { InputsReducer, InputValue } from 'src/redux/reducers/inputs';
import {
  QueryParamsReducer,
  QueryParams,
} from 'src/redux/reducers/query-params';
import { sanitizeInputValue } from 'src/utils/inputs';
import { isEmpty } from 'lodash';

export const sanitizeSearchParams = (
  searchParams: SearchParams
): SearchParams => {
  const { securityDeposit, ...others } = searchParams;
  const params =
    searchParams.primaryGoal !== PrimaryGoal.BuildCredit
      ? others
      : searchParams;

  if (searchParams.primaryGoal === PrimaryGoal.BigPurchase) {
    const { monthlySpend, ...rest } = params;
    return rest;
  }
  if (
    searchParams.primaryGoal === PrimaryGoal.CashBack ||
    searchParams.primaryGoal === PrimaryGoal.Travel ||
    searchParams.primaryGoal === PrimaryGoal.Debt
  ) {
    const { purchaseSpend, ...rest } = params;
    return rest;
  }

  return params;
};

type SearchParams = Record<string, any>;
export const buildQueryString = (searchParams?: SearchParams) => {
  const sanitizedSearchParams = searchParams
    ? sanitizeSearchParams(searchParams)
    : {};
  const params = new URLSearchParams();
  Object.entries(sanitizedSearchParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (typeof v === 'object') {
          const stringifiedProps = Object.values(v).join('-');
          params.append(key, stringifiedProps);
        } else {
          params.append(key, v);
        }
      });
    } else if (value !== undefined) {
      params.append(key, value ?? '');
    }
  });

  const stringified = params.toString();

  return stringified ? `?${stringified}` : '';
};

export const parseInputsFromQueryString = (
  searchParams: Record<string, string | string[] | undefined>
): InputsReducer => {
  return Object.keys(searchParams).reduce((acc, key) => {
    const inputKey = key as Input;
    if (Inputs.hasOwnProperty(key)) {
      const value = sanitizeInputValue(inputKey, searchParams[key], true);
      if (typeof value !== 'undefined' && value !== null) {
        // @ts-expect-error
        acc[inputKey] = value;
      }
    }

    return acc;
  }, {} as InputsReducer);
};

export const getInputsFromQueryString = (
  searchParams: Record<string, string | string[] | undefined>
): InputsReducer => {
  const inputs = parseInputsFromQueryString(searchParams);
  if (inputs[Input.CreditScore]) {
    if (inputs[Input.CreditScore] === CreditScore.Poor) {
      inputs[Input.PrimaryGoal] = PrimaryGoal.BuildCredit;
    } else if (
      inputs[Input.CreditScore] !== CreditScore.Average &&
      inputs[Input.CreditScore] !== CreditScore.LimitedCreditHistory &&
      inputs[Input.PrimaryGoal] === PrimaryGoal.BuildCredit
    ) {
      inputs[Input.PrimaryGoal] = undefined;
    }
  }

  return inputs;
};

export const getExperimentOverrides = (
  searchParams: Record<string, string | string[] | undefined>
): QueryParams =>
  Object.entries(searchParams).reduce((acc, [key, value]) => {
    if (key.startsWith('test_') && typeof value === 'string') {
      acc[key] = value as InputValue;
    }
    return acc;
  }, {} as QueryParams);

export const getExcludedInstitutions = (
  searchParams: Record<string, string | string[] | undefined>
): string[] =>
  (Array.isArray(searchParams.exclude_institution)
    ? searchParams.exclude_institution
    : [searchParams.exclude_institution]
  ).filter((value) => typeof value === 'string') as string[];

export const convertArrayType = (
  param: string | string[] | undefined
): string | undefined => {
  let convertedString: string | undefined;
  if (param instanceof Array) {
    // shouldn't happen but should be handled anyways
    convertedString = param.join(', ');
  } else if (typeof param === 'string') {
    convertedString = param;
  }
  return convertedString;
};

export const getInputsFromAuth0AppState = (
  appState?: string | string[]
): InputsReducer | null => {
  if (!appState || Array.isArray(appState)) return null;
  if (isValidJson(appState)) {
    const parsedState = JSON.parse(appState);
    if (Object.hasOwn(parsedState, 'inputs')) {
      const inputs = parseInputsFromQueryString(parsedState.inputs);
      return !isEmpty(inputs) ? inputs : null;
    }
  }
  return null;
};

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

export const getQueryParams = (
  searchParams: Record<string, string | string[] | undefined>
): QueryParamsReducer => ({
  inputs: getInputsFromQueryString(searchParams),
  excludeInstitution: getExcludedInstitutions(searchParams),
  experimentOverrides: getExperimentOverrides(searchParams),
  isMobileApp: searchParams?.trk_source === 'mobile',
  auth0State: searchParams?.appState,
  auth0AnonymousId: searchParams?.anonymousId,
  auth0Variant: searchParams?.auth0Variant,
});

export const parseQueryParams = (
  searchParams: URLSearchParams
): Record<string, string | string[]> => {
  const queryParams: Record<string, string | string[]> = {};
  searchParams.forEach((value, key) => {
    if (queryParams[key]) {
      if (!Array.isArray(queryParams[key])) {
        queryParams[key] = [queryParams[key] as string];
      }
      (queryParams[key] as string[]).push(value);
    } else {
      queryParams[key] = value;
    }
  });

  return queryParams;
};
